import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import {
    collection, query, where, orderBy, limit, startAfter, getDocs, doc, setDoc, addDoc, updateDoc,
    increment, Timestamp, serverTimestamp,
} from 'firebase/firestore';
import {
    Menu, LogOut, Search, RefreshCw, Filter, PhoneCall, PhoneOff, PhoneIncoming, MessageCircle,
    Eye, ShieldAlert, StickyNote, ShoppingBag, ShoppingCart, CheckCircle2, Copy, History,
    CalendarDays,
} from 'lucide-react';
import DashboardNav from './DashboardNav';
import { toLocalPhone } from './CallHistory';
import {
    CallTriesModal, NotesModal, WhatsAppClicksModal, FraudModal, OrdersModal, SellInfoModal,
    STATUS_STYLE, toMs, fmtDateTime, fmtDuration, callMillis,
} from './WithoutOrderModals';
import { auth, db } from './firebase';
import { updateAdminSalary, hasRecentAnsweredCall } from './adminSalary';

/**
 * ─────────────────────────────────────────────────────────────
 *  Without Orders — যারা cart এ product রেখেছে কিন্তু অর্ডার করেনি
 *  (Flutter admin app এর WithoutOrdersScreen এর সব feature + কল ট্র্যাকিং)
 *
 *  ডাটা:
 *   • withoutorders              ← lead এর তালিকা (created_at দিয়ে নতুন আগে)
 *   • callRecords                ← কল সার্ভারের (call-history.js) সব কল; number = 01XXXXXXXXX
 *                                  একটা lead "কল হয়েছে" ধরা হয় যদি lead তৈরির পরে (created_at ≥)
 *                                  ওই নাম্বারে অন্তত একটা কল রেকর্ড থাকে। ধরুক বা না ধরুক — try গোনা হয়।
 *   • withoutorders/{id}/notes, whatsappclick, call_history (সাবকালেকশন)
 *
 *  লেখা হয় শুধু: নোট, WhatsApp click/counter, Call বাটনের counter, reminder toggle, sell ফ্ল্যাগ (Create Order থেকে)
 * ─────────────────────────────────────────────────────────────
 */

const FLASK_BASE_URL = 'https://elevatemart-main-server.sparkx-ai.shop';
const COUPON = 'ELEVATE70';
const PAGE_SIZE = 500;     // withoutorders একেকবারে এতগুলো করে পড়া হয়
const HARD_LIMIT = 6000;   // এর বেশি পড়া হবে না (read খরচ আটকাতে)

const pad = (n) => String(n).padStart(2, '0');
const isYMD = (v) => /^\d{4}-\d{2}-\d{2}$/.test(String(v || ''));

/** বাংলাদেশের (UTC+6) তারিখ 'YYYY-MM-DD', offset দিন আগে/পরে */
const dhakaYMD = (offsetDays = 0) => {
    const d = new Date(Date.now() + 6 * 3600 * 1000 + offsetDays * 86400000);
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
};
/** ms → বাংলাদেশের তারিখ 'YYYY-MM-DD' (ব্রাউজারের টাইমজোন যা-ই হোক) */
const ymdOfMs = (ms) => {
    const d = new Date(ms + 6 * 3600 * 1000);
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
};
const dayStartMs = (ymd) => Date.parse(`${ymd}T00:00:00+06:00`);
const dayEndMs = (ymd) => Date.parse(`${ymd}T23:59:59.999+06:00`);
const fmtDay = (ymd) => `${ymd.slice(8, 10)}/${ymd.slice(5, 7)}/${ymd.slice(0, 4)}`;

/** 'yyyy-MM-dd' → 'dd-MM-yyyy' (Dart এর next_reminder_date এর মতো) */
const fmtReminder = (v) => {
    if (!v) return 'N/A';
    if (typeof v === 'string') {
        const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
        return m ? `${m[3]}-${m[2]}-${m[1]}` : v;
    }
    const ms = toMs(v);
    return ms ? fmtDateTime(ms).slice(0, 10) : 'N/A';
};

/* ───────────────────────── data loading ───────────────────────── */

/**
 * withoutorders নতুন থেকে পুরনো — fromMs এর চেয়ে পুরনো ডক পাওয়া পর্যন্ত পাতা ধরে পড়া।
 * (created_at Timestamp বা String যেটাই হোক orderBy একইভাবে কাজ করে — Flutter স্ক্রিনের মতোই)
 */
async function loadWithoutOrders(fromMs) {
    const docs = [];
    let last = null;
    let truncated = false;
    for (;;) {
        const q = last
            ? query(collection(db, 'withoutorders'), orderBy('created_at', 'desc'), startAfter(last), limit(PAGE_SIZE))
            : query(collection(db, 'withoutorders'), orderBy('created_at', 'desc'), limit(PAGE_SIZE));
        const snap = await getDocs(q);
        snap.docs.forEach((d) => docs.push({ ...d.data(), id: d.id }));
        if (snap.size < PAGE_SIZE) break;
        last = snap.docs[snap.docs.length - 1];
        const oldest = toMs(last.get('created_at'));
        if (oldest && oldest < fromMs) break;
        if (docs.length >= HARD_LIMIT) { truncated = true; break; }
    }
    return { docs, truncated };
}

/** callRecords.date >= fromYMD — একটাই কুয়েরি (Call Report পেজের মতোই) */
async function loadCalls(fromYMD) {
    const snap = await getDocs(query(collection(db, 'callRecords'), where('date', '>=', fromYMD)));
    return snap.docs.map((d) => ({ ...d.data(), id: d.id }));
}

async function loadAll(fromYMD) {
    const fromMs = dayStartMs(fromYMD);
    const [wo, calls] = await Promise.allSettled([loadWithoutOrders(fromMs), loadCalls(fromYMD)]);
    if (wo.status === 'rejected') console.error('withoutorders read failed:', wo.reason);
    if (calls.status === 'rejected') console.error('callRecords read failed:', calls.reason);
    return {
        docs: wo.status === 'fulfilled' ? wo.value.docs : [],
        truncated: wo.status === 'fulfilled' ? wo.value.truncated : false,
        docsFailed: wo.status === 'rejected',
        calls: calls.status === 'fulfilled' ? calls.value : [],
        callsFailed: calls.status === 'rejected',
    };
}

/* ───────────────────────── small UI helpers ───────────────────────── */

const CALL_FILTERS = [
    { k: 'all', l: 'সব' },
    { k: 'not_called', l: 'কল দেওয়া হয়নি' },
    { k: 'called', l: 'কল দেওয়া হয়েছে (১+ বার)' },
    { k: 'answered', l: 'ধরেছে (Answered)' },
    { k: 'no_answer', l: 'কল দিয়েছে কিন্তু একবারও ধরেনি' },
    { k: 'multi', l: '২+ বার try করা হয়েছে' },
];

const matchCallFilter = (r, f) => {
    if (f === 'not_called') return r.attempts === 0;
    if (f === 'called') return r.attempts > 0;
    if (f === 'answered') return r.answeredCount > 0;
    if (f === 'no_answer') return r.attempts > 0 && r.answeredCount === 0;
    if (f === 'multi') return r.attempts >= 2;
    return true;
};

function IconBtn({ title, onClick, children, tone = 'green' }) {
    const tones = {
        green: 'text-green-600 hover:bg-green-50',
        blue: 'text-blue-600 hover:bg-blue-50',
        orange: 'text-orange-600 hover:bg-orange-50',
        red: 'text-red-600 hover:bg-red-50',
    };
    return (
        <button
            type="button"
            title={title}
            onClick={onClick}
            className={`p-1.5 rounded-lg transition ${tones[tone]}`}
        >
            {children}
        </button>
    );
}

/* ───────────────────────── page ───────────────────────── */

function WithoutOrdersPage() {
    const navigate = useNavigate();
    const [userEmail, setUserEmail] = useState('');
    const [sidebarOpen, setSidebarOpen] = useState(() => typeof window === 'undefined' || window.innerWidth >= 1024);

    const [preset, setPreset] = useState('today');
    const [from, setFrom] = useState(() => dhakaYMD(0));
    const [to, setTo] = useState(() => dhakaYMD(0));
    const [reload, setReload] = useState(0);
    const [state, setState] = useState({
        loading: true, docs: [], calls: [], truncated: false, docsFailed: false, callsFailed: false,
    });

    const [callF, setCallF] = useState('all');
    const [dayF, setDayF] = useState('');
    const [search, setSearch] = useState('');
    const [visible, setVisible] = useState(50);

    const [patches, setPatches] = useState({}); // docId → পেজে বসে করা বদলের লোকাল কপি
    const [modal, setModal] = useState(null);   // { type, row }
    const [toast, setToast] = useState(null);
    const toastTimer = useRef(null);

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, (user) => {
            if (user) setUserEmail(user.email || '');
            else navigate('/login');
        });
        return () => unsub();
    }, [navigate]);

    useEffect(() => {
        if (!isYMD(from)) return undefined;
        let alive = true;
        setState((s) => ({ ...s, loading: true }));
        loadAll(from)
            .then((r) => { if (alive) setState({ loading: false, ...r }); })
            .catch((e) => {
                console.error(e);
                if (alive) setState({ loading: false, docs: [], calls: [], truncated: false, docsFailed: true, callsFailed: true });
            });
        return () => { alive = false; };
    }, [from, reload]);

    useEffect(() => { setVisible(50); }, [callF, dayF, search, from, to]);
    useEffect(() => () => clearTimeout(toastTimer.current), []);

    const showToast = (msg, type = 'ok') => {
        clearTimeout(toastTimer.current);
        setToast({ msg, type });
        toastTimer.current = setTimeout(() => setToast(null), 3000);
    };

    const applyPreset = (p) => {
        setPreset(p);
        setDayF('');
        if (p === 'today') { setFrom(dhakaYMD(0)); setTo(dhakaYMD(0)); }
        if (p === 'yesterday') { setFrom(dhakaYMD(-1)); setTo(dhakaYMD(-1)); }
        if (p === '7d') { setFrom(dhakaYMD(-6)); setTo(dhakaYMD(0)); }
        if (p === '30d') { setFrom(dhakaYMD(-29)); setTo(dhakaYMD(0)); }
    };

    const handleLogout = async () => {
        try {
            await signOut(auth);
            navigate('/login');
        } catch (error) {
            console.error('Error logging out:', error);
        }
    };

    /* ── নাম্বার অনুযায়ী কলের তালিকা (পুরনো → নতুন) ── */
    const callsByPhone = useMemo(() => {
        const map = new Map();
        state.calls.forEach((c) => {
            const k = toLocalPhone(c.number);
            if (k.length < 10) return;
            if (!map.has(k)) map.set(k, []);
            map.get(k).push({ ...c, ms: callMillis(c) });
        });
        map.forEach((list) => list.sort((a, b) => a.ms - b.ms));
        return map;
    }, [state.calls]);

    /* ── তারিখের রেঞ্জের lead গুলো + প্রতিটার কল-তথ্য ── */
    const rangeRows = useMemo(() => {
        if (!isYMD(from) || !isYMD(to) || from > to) return [];
        const fromMs = dayStartMs(from);
        const toMsEnd = dayEndMs(to);
        const rows = [];
        state.docs.forEach((d) => {
            const createdMs = toMs(d.created_at);
            if (!createdMs || createdMs < fromMs || createdMs > toMsEnd) return;
            const phone = String(d.phone || '').trim();
            const local = toLocalPhone(phone);
            const tries = (callsByPhone.get(local) || []).filter((c) => c.ms >= createdMs);
            rows.push({
                id: d.id,
                clientId: d.client_id || d.id,
                phone,
                local,
                name: d.name || 'N/A',
                address: d.address || '',
                createdMs,
                createdRaw: typeof d.created_at === 'string' ? d.created_at : '',
                day: ymdOfMs(createdMs),
                serverCallDate: d.ServerAPICallingDate || 'N/A',
                reminderCount: d.reminder_count || 0,
                nextReminder: fmtReminder(d.next_reminder_date),
                whatsAppCount: d.whats_app || 0,
                lastCommunicateMs: toMs(d.last_communicate),
                cartItems: Array.isArray(d.cart_items) ? d.cart_items : [],
                sell: d.sell === true,
                sendReminders: d.send_reminders !== false,
                tries,
                attempts: tries.length,
                answeredCount: tries.filter((c) => c.answered).length,
                lastTry: tries.length ? tries[tries.length - 1] : null,
            });
        });
        return rows.sort((a, b) => b.createdMs - a.createdMs);
    }, [state.docs, callsByPhone, from, to]);

    /* ── সামারি + দিন-ভিত্তিক হিসাব (শুধু তারিখ রেঞ্জ দিয়ে) ── */
    const stats = useMemo(() => {
        const sum = {
            total: rangeRows.length,
            unique: new Set(rangeRows.map((r) => r.local || r.id)).size,
            called: 0, answered: 0, notCalled: 0, tries: 0, sold: 0,
        };
        const dayMap = new Map();
        rangeRows.forEach((r) => {
            if (!dayMap.has(r.day)) {
                dayMap.set(r.day, { day: r.day, came: 0, called: 0, answered: 0, notCalled: 0, tries: 0, sold: 0 });
            }
            const d = dayMap.get(r.day);
            d.came += 1;
            sum.tries += r.attempts;
            d.tries += r.attempts;
            if (r.attempts > 0) { sum.called += 1; d.called += 1; } else { sum.notCalled += 1; d.notCalled += 1; }
            if (r.answeredCount > 0) { sum.answered += 1; d.answered += 1; }
            if (r.sell) { sum.sold += 1; d.sold += 1; }
        });
        const days = Array.from(dayMap.values()).sort((a, b) => (a.day < b.day ? 1 : -1));
        return { sum, days };
    }, [rangeRows]);

    /* ── টেবিলের তালিকা: কল-ফিল্টার + দিন + সার্চ ── */
    const rows = useMemo(() => {
        const q = search.trim().toLowerCase();
        const qDigits = q.replace(/\D/g, '');
        return rangeRows.filter((r) => {
            if (dayF && r.day !== dayF) return false;
            if (!matchCallFilter(r, callF)) return false;
            if (q) {
                const hitPhone = r.phone.toLowerCase().includes(q) || (qDigits.length >= 3 && r.local.includes(qDigits));
                const hitName = r.name.toLowerCase().includes(q);
                if (!hitPhone && !hitName) return false;
            }
            return true;
        });
    }, [rangeRows, dayF, callF, search]);

    const hasFilter = callF !== 'all' || dayF || search.trim();
    const clearFilters = () => { setCallF('all'); setDayF(''); setSearch(''); };

    /* ── actions ── */

    const copyText = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            showToast(`📋 Copied: ${text}`);
        } catch (e) {
            showToast('Copy করা যায়নি', 'err');
        }
    };

    const patchRow = (id, p) => setPatches((m) => ({ ...m, [id]: { ...(m[id] || {}), ...p } }));

    /**
     * Flutter এর switch এর salary নিয়ম (Action: 'Non Order Toogle Off'):
     *   নাম্বার ≥ ১১ ডিজিট হলে — শেষ ২ দিনে ওই নাম্বারে Answered কল থাকলে admin এর salary +২, না থাকলে −২।
     *   (Flutter এ switch চালু/বন্ধ দুই দিকেই এটা চলে; এখানেও তাই। শুধু toggle সফল হলে চলে।)
     *   কল-রেকর্ড পড়া ব্যর্থ হলে salary ছোঁয়া হয় না — ভুল কাটা ঠেকাতে।
     */
    const applyToggleSalary = async (r) => {
        const email = auth.currentUser?.email;
        if (!email || !r.phone || r.phone.length < 11) return;
        let answered;
        try {
            answered = await hasRecentAnsweredCall(r.phone, 2);
        } catch (e) {
            console.error('callRecords check failed:', e);
            showToast('কল রেকর্ড পড়া যায়নি — salary বদলানো হয়নি', 'err');
            return;
        }
        const sal = await updateAdminSalary({
            amount: 2,
            actionName: 'Non Order Toogle Off',
            isAddition: answered,
            staffEmail: email,
        });
        if (sal.success) showToast(`Salary ${answered ? '+' : '−'}২ (${answered ? 'শেষ ২ দিনে ধরা কল আছে' : 'শেষ ২ দিনে ধরা কল নেই'})`, answered ? 'ok' : 'err');
        else showToast(`Salary আপডেট হয়নি: ${sal.message}`, 'err');
    };

    const toggleReminders = async (r, enable) => {
        patchRow(r.id, { sendReminders: enable });
        try {
            const res = await fetch(`${FLASK_BASE_URL}/toggle_reminders/${r.id}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json; charset=UTF-8' },
                body: JSON.stringify({ send_reminders: enable }),
            });
            if (res.ok) {
                showToast(`রিমাইন্ডার সিস্টেম "${r.id}" এর জন্য ${enable ? 'চালু' : 'বন্ধ'} হয়েছে।`);
                await applyToggleSalary(r);
            } else {
                patchRow(r.id, { sendReminders: !enable });
                showToast(`রিমাইন্ডার টগল করতে ব্যর্থ: ${await res.text()}`, 'err');
            }
        } catch (e) {
            patchRow(r.id, { sendReminders: !enable });
            showToast(`নেটওয়ার্ক ত্রুটি: ${e.message}`, 'err');
        }
    };

    const sendWhatsApp = async (r) => {
        if (!r.phone) {
            showToast('এই অর্ডারের জন্য কোন ফোন নম্বর নেই।', 'err');
            return;
        }
        // ক্লিকের সাথে সাথেই ট্যাব খুলে রাখি, নাহলে নিচের await এর পর ব্রাউজার popup ব্লক করে
        const win = window.open('', '_blank');

        const email = auth.currentUser?.email;
        if (email) {
            try {
                await addDoc(collection(db, 'withoutorders', r.id, 'whatsappclick'), {
                    clickedBy: email,
                    clickedAt: serverTimestamp(),
                });
            } catch (e) {
                console.error('whatsappclick write failed:', e);
            }
        }

        let p = r.phone.trim();
        if (!p.startsWith('+')) p = (p.startsWith('88') && p.length >= 12) ? `+${p}` : `+88${p}`;

        const names = r.cartItems.map((it) => it?.name || 'পণ্য');
        const urls = [];
        await Promise.all(r.cartItems.map(async (it) => {
            const pname = it?.name;
            if (!pname) return;
            try {
                const snap = await getDocs(query(collection(db, 'products'), where('name', '==', pname), limit(1)));
                if (!snap.empty) {
                    urls.push(`https://elevateemart.store/product/${snap.docs[0].id}/${String(pname).replace(/ /g, '-').toLowerCase()}`);
                }
            } catch (e) {
                console.error('product lookup failed:', e);
            }
        }));

        const productList = names.length ? names.join(', ') : 'কিছু পণ্য';
        const productLinks = urls.length ? urls.join('\n') : 'পণ্যের কোনো লিঙ্ক পাওয়া যায়নি।';
        const message =
            `হ্যালো স্যার ${r.name},\n`
            + `*ElevateMart* এ আপনাকে স্বাগতম! আপনি আমাদের শপ থেকে আপনার পছন্দের পণ্যগুলো (${productList}) অর্ডার করতে চেয়েছিলেন।\n`
            + `আমরা আপনাকে একটি কুপন দিচ্ছি🔥: *${COUPON}* যা ব্যবহার করে আপনি আকর্ষণীয় ডিসকাউন্ট পেতে পারেন।\n`
            + `পণ্য দেখতে ভিজিট করুন:\n${productLinks}\n`
            + '*ক্যাশ অন ডেলিভারি* সুবিধা\n'
            + '৭ দিনের *Easy Return* গ্যারান্টি\n'
            + 'এখনই অর্ডার করতে কল করুন: *+8809647001250*\n'
            + 'দ্রুত ডেলিভারি, মানের নিশ্চয়তা!';
        const url = `https://wa.me/${p}?text=${encodeURIComponent(message)}`;

        if (win) win.location.href = url; else window.open(url, '_blank');

        try {
            await updateDoc(doc(db, 'withoutorders', r.id), {
                whats_app: increment(1),
                last_communicate: Timestamp.now(),
            });
            patchRow(r.id, { whatsAppCount: r.whatsAppCount + 1, lastCommunicateMs: Date.now() });
            showToast('WhatsApp এবং last communicate আপডেট হয়েছে।');
        } catch (e) {
            showToast(`আপডেট করতে ব্যর্থ: ${e.message}`, 'err');
        }
    };

    /** Flutter এর Call বাটন: সফটফোন (sip:) খোলে + Firebase এ communication counter বাড়ায় */
    const startCall = async (r) => {
        if (!r.phone) return;
        const a = document.createElement('a');
        a.href = `sip:${r.phone}`;
        a.click();
        try {
            await setDoc(doc(collection(db, 'withoutorders', r.clientId, 'call_history')), {
                communication: increment(1),
                next_calling_date: Timestamp.fromDate(new Date(Date.now() + 86400000)),
            }, { merge: true });
            await setDoc(doc(db, 'withoutorders', r.clientId), { calling_date: Timestamp.now() }, { merge: true });
        } catch (e) {
            console.error('Call counter update failed:', e);
            showToast(`Call counter আপডেট হয়নি: ${e.message}`, 'err');
        }
    };

    const openCreateOrder = (r) => {
        const productIds = r.cartItems.map((it) => it?.productId || it?.product_id || it?.id).filter(Boolean);
        navigate('/create-order', {
            state: {
                prefill: { name: r.name === 'N/A' ? '' : r.name, phone: r.phone, address: r.address, notes: '', productIds },
                withoutOrderId: r.id,
                withoutOrderPhone: r.phone,
            },
        });
    };

    const openFraud = (r) => {
        if (r.phone && r.phone.length >= 11) setModal({ type: 'fraud', row: r });
        else showToast('ফোন নম্বর সঠিক নয় (কমপক্ষে ১১ ডিজিট লাগবে)', 'err');
    };

    /* ── render ── */

    const PRESETS = [
        { k: 'today', l: 'আজ' }, { k: 'yesterday', l: 'গতকাল' },
        { k: '7d', l: 'শেষ ৭ দিন' }, { k: '30d', l: 'শেষ ৩০ দিন' }, { k: 'custom', l: 'Custom' },
    ];
    const { loading, docsFailed, callsFailed, truncated } = state;
    const { sum, days } = stats;
    const pct = (n) => (sum.total ? Math.round((n / sum.total) * 100) : 0);

    const inputCls = 'w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-400';

    return (
        <div className="min-h-screen bg-gray-50 flex text-gray-900">
            <DashboardNav sidebarOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

            <div className="flex-1 min-w-0 p-6 overflow-auto">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => setSidebarOpen(!sidebarOpen)}
                                className="p-2 hover:bg-orange-50 rounded-lg transition"
                            >
                                <Menu size={24} />
                            </button>
                            <div>
                                <h1 className="text-2xl md:text-4xl font-bold bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">
                                    Without Orders
                                </h1>
                                <p className="text-sm text-gray-500">যারা অর্ডার করেনি · Logged in as: {userEmail}</p>
                            </div>
                        </div>
                        <button
                            onClick={handleLogout}
                            className="flex items-center gap-2 bg-orange-400 text-white hover:bg-orange-500 px-4 py-2 rounded-lg transition duration-200"
                        >
                            <LogOut size={20} />
                            Logout
                        </button>
                    </div>

                    {/* Filters */}
                    <div className="bg-white rounded-xl p-5 mb-5 border border-gray-200">
                        <div className="flex items-center gap-2 mb-3">
                            <Filter size={18} className="text-orange-600" />
                            <h3 className="text-base font-semibold">Filters</h3>
                            <button
                                onClick={() => setReload((n) => n + 1)}
                                className="ml-auto flex items-center gap-1.5 text-sm text-orange-700 bg-orange-100 hover:bg-orange-200 px-3 py-1.5 rounded-lg transition"
                            >
                                <RefreshCw size={14} /> Refresh
                            </button>
                        </div>

                        <div className="flex flex-wrap gap-2 mb-3">
                            {PRESETS.map((p) => (
                                <button
                                    key={p.k}
                                    onClick={() => (p.k === 'custom' ? setPreset('custom') : applyPreset(p.k))}
                                    className={`px-4 py-1.5 text-sm font-semibold rounded-lg border transition ${preset === p.k
                                        ? 'bg-orange-400 text-white border-orange-400'
                                        : 'bg-white text-gray-600 border-gray-300 hover:bg-orange-50'}`}
                                >
                                    {p.l}
                                </button>
                            ))}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                            <div>
                                <label className="block text-xs text-gray-500 mb-1">From (lead এসেছে)</label>
                                <input type="date" value={from} className={inputCls}
                                    onChange={(e) => { setPreset('custom'); setDayF(''); setFrom(e.target.value); }} />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-500 mb-1">To</label>
                                <input type="date" value={to} className={inputCls}
                                    onChange={(e) => { setPreset('custom'); setDayF(''); setTo(e.target.value); }} />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-500 mb-1">কল স্ট্যাটাস</label>
                                <select value={callF} onChange={(e) => setCallF(e.target.value)} className={inputCls}>
                                    {CALL_FILTERS.map((f) => <option key={f.k} value={f.k}>{f.l}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs text-gray-500 mb-1">ফোন নম্বর / নাম</label>
                                <div className="relative">
                                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input
                                        value={search} onChange={(e) => setSearch(e.target.value)}
                                        placeholder="ফোন নম্বর দিয়ে সার্চ করুন..."
                                        className={`${inputCls} pl-8`}
                                    />
                                </div>
                            </div>
                        </div>

                        {hasFilter && (
                            <button onClick={clearFilters} className="mt-3 text-sm text-orange-700 hover:underline">
                                সব ফিল্টার মুছুন
                            </button>
                        )}
                    </div>

                    {docsFailed && (
                        <div className="mb-5 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">
                            withoutorders পড়া যায়নি — Firestore rules এ withoutorders পড়ার অনুমতি লাগবে।
                        </div>
                    )}
                    {!loading && callsFailed && !docsFailed && (
                        <div className="mb-5 p-3 bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-lg">
                            callRecords পড়া যায়নি — Firestore rules এ callRecords পড়ার অনুমতি লাগবে। কল-সংখ্যা ভুল (০) দেখাবে।
                        </div>
                    )}
                    {!loading && truncated && (
                        <div className="mb-5 p-3 bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-lg">
                            ডাটা বেশি, তাই নতুন {HARD_LIMIT}টা lead পর্যন্ত পড়া হয়েছে — তারিখ রেঞ্জ ছোট করুন।
                        </div>
                    )}

                    {loading ? (
                        <div className="flex justify-center items-center h-56">
                            <div className="animate-spin rounded-full h-14 w-14 border-t-4 border-orange-500"></div>
                        </div>
                    ) : (
                        <>
                            {/* Summary cards */}
                            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
                                <button
                                    onClick={() => setCallF('all')}
                                    className={`text-left bg-orange-50 border border-orange-200 rounded-xl p-4 transition hover:shadow ${callF === 'all' ? 'ring-2 ring-orange-400' : ''}`}
                                >
                                    <div className="text-2xl font-bold text-gray-900">{sum.total}</div>
                                    <div className="text-xs text-gray-500">মোট Without Order</div>
                                    <div className="text-xs text-gray-400 mt-0.5">{sum.unique} টি ইউনিক নম্বর</div>
                                </button>
                                <button
                                    onClick={() => setCallF(callF === 'called' ? 'all' : 'called')}
                                    className={`text-left bg-blue-50 border border-blue-200 rounded-xl p-4 transition hover:shadow ${callF === 'called' ? 'ring-2 ring-orange-400' : ''}`}
                                >
                                    <div className="text-2xl font-bold text-gray-900">{sum.called}</div>
                                    <div className="text-xs text-gray-500">কল দেওয়া হয়েছে</div>
                                    <div className="text-xs text-gray-400 mt-0.5">{pct(sum.called)}%</div>
                                </button>
                                <button
                                    onClick={() => setCallF(callF === 'answered' ? 'all' : 'answered')}
                                    className={`text-left bg-green-50 border border-green-200 rounded-xl p-4 transition hover:shadow ${callF === 'answered' ? 'ring-2 ring-orange-400' : ''}`}
                                >
                                    <div className="text-2xl font-bold text-gray-900">{sum.answered}</div>
                                    <div className="text-xs text-gray-500">কল ধরেছে</div>
                                    <div className="text-xs text-gray-400 mt-0.5">{pct(sum.answered)}%</div>
                                </button>
                                <button
                                    onClick={() => setCallF(callF === 'not_called' ? 'all' : 'not_called')}
                                    className={`text-left bg-red-50 border border-red-200 rounded-xl p-4 transition hover:shadow ${callF === 'not_called' ? 'ring-2 ring-orange-400' : ''}`}
                                >
                                    <div className="text-2xl font-bold text-red-700">{sum.notCalled}</div>
                                    <div className="text-xs text-gray-500">কল দেওয়া হয়নি</div>
                                    <div className="text-xs text-gray-400 mt-0.5">{pct(sum.notCalled)}%</div>
                                </button>
                                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                                    <div className="text-2xl font-bold text-gray-900">{sum.tries}</div>
                                    <div className="text-xs text-gray-500">মোট try (কল)</div>
                                    <div className="text-xs text-gray-400 mt-0.5">Sell: {sum.sold}</div>
                                </div>
                            </div>

                            {/* Per-day summary */}
                            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-5">
                                <div className="px-4 py-3 border-b border-gray-200 font-semibold text-gray-800 flex items-center gap-2">
                                    <CalendarDays size={18} className="text-orange-500" />
                                    দিন অনুযায়ী: কতজন এসেছে, কতজনকে কল দেওয়া হয়েছে
                                    <span className="text-xs font-normal text-gray-500">(দিনে চাপলে ওই দিনের তালিকা ফিল্টার হবে)</span>
                                </div>
                                {days.length === 0 ? (
                                    <p className="text-center text-gray-500 py-8 text-sm">এই সময়ে কোনো Without Order নেই</p>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead className="bg-gray-50 text-gray-600">
                                                <tr>
                                                    <th className="px-4 py-3 text-left font-semibold">তারিখ</th>
                                                    <th className="px-3 py-3 text-right font-semibold">এসেছে</th>
                                                    <th className="px-3 py-3 text-right font-semibold">কল হয়েছে</th>
                                                    <th className="px-3 py-3 text-right font-semibold">ধরেছে</th>
                                                    <th className="px-3 py-3 text-right font-semibold">কল হয়নি</th>
                                                    <th className="px-3 py-3 text-right font-semibold">মোট try</th>
                                                    <th className="px-3 py-3 text-right font-semibold">Sell</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                                {days.map((d) => (
                                                    <tr key={d.day} className={`hover:bg-orange-50 ${dayF === d.day ? 'bg-orange-50' : ''}`}>
                                                        <td className="px-4 py-3">
                                                            <button
                                                                onClick={() => setDayF(dayF === d.day ? '' : d.day)}
                                                                className="font-semibold text-orange-700 hover:underline"
                                                            >
                                                                {fmtDay(d.day)}
                                                            </button>
                                                        </td>
                                                        <td className="px-3 py-3 text-right font-semibold">{d.came}</td>
                                                        <td className="px-3 py-3 text-right">
                                                            {d.called}
                                                            <span className="text-xs text-gray-400"> ({d.came ? Math.round((d.called / d.came) * 100) : 0}%)</span>
                                                        </td>
                                                        <td className="px-3 py-3 text-right">{d.answered}</td>
                                                        <td className="px-3 py-3 text-right">
                                                            {d.notCalled > 0 ? (
                                                                <button
                                                                    onClick={() => { setDayF(d.day); setCallF('not_called'); }}
                                                                    className="font-semibold text-red-600 hover:underline"
                                                                    title="এই দিনের কল-না-দেওয়া তালিকা"
                                                                >
                                                                    {d.notCalled}
                                                                </button>
                                                            ) : <span className="text-green-600 font-semibold">0</span>}
                                                        </td>
                                                        <td className="px-3 py-3 text-right">{d.tries}</td>
                                                        <td className="px-3 py-3 text-right">{d.sold}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        <p className="px-4 py-2 text-xs text-gray-400 border-t border-gray-100">
                                            "কল হয়েছে" = lead তৈরির পরে কল সার্ভার থেকে ওই নাম্বারে অন্তত একবার কল গেছে (ধরুক বা না ধরুক)।
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Table */}
                            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                                <div className="px-4 py-3 border-b border-gray-200 flex flex-wrap items-center gap-2">
                                    <span className="font-semibold text-gray-800">তালিকা</span>
                                    <span className="text-xs text-gray-500">({rows.length} টি)</span>
                                    {dayF && <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">{fmtDay(dayF)}</span>}
                                    {callF !== 'all' && (
                                        <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">
                                            {CALL_FILTERS.find((f) => f.k === callF)?.l}
                                        </span>
                                    )}
                                </div>

                                {rows.length === 0 ? (
                                    <p className="text-center text-gray-500 py-10 text-sm">
                                        {rangeRows.length === 0 ? 'কোনো "Without Orders" ডেটা পাওয়া যায়নি।' : 'ফিল্টারে কোনো ডেটা পাওয়া যায়নি।'}
                                    </p>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm min-w-[1250px]">
                                            <thead className="bg-gray-50 text-gray-600">
                                                <tr>
                                                    <th className="px-3 py-3 text-left font-semibold">SL</th>
                                                    <th className="px-3 py-3 text-left font-semibold">Phone / Name</th>
                                                    <th className="px-3 py-3 text-left font-semibold">Created At</th>
                                                    <th className="px-3 py-3 text-left font-semibold">Call Tries</th>
                                                    <th className="px-3 py-3 text-left font-semibold">Server Call Date</th>
                                                    <th className="px-3 py-3 text-left font-semibold">Reminders</th>
                                                    <th className="px-3 py-3 text-left font-semibold">Last Communicate</th>
                                                    <th className="px-3 py-3 text-left font-semibold">Actions</th>
                                                    <th className="px-3 py-3 text-left font-semibold">Cart Items</th>
                                                    <th className="px-3 py-3 text-center font-semibold">Sell</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                                {rows.slice(0, visible).map((base, i) => {
                                                    const r = { ...base, ...(patches[base.id] || {}) };
                                                    const last = r.lastTry;
                                                    return (
                                                        <tr key={r.id} className="hover:bg-orange-50 align-top">
                                                            <td className="px-3 py-3 text-gray-500">{i + 1}</td>

                                                            <td className="px-3 py-3">
                                                                <button onClick={() => copyText(r.phone)} className="flex items-center gap-1 font-semibold text-green-700 hover:underline" title="Copy">
                                                                    {r.phone || 'N/A'} <Copy size={11} className="text-gray-400" />
                                                                </button>
                                                                <button onClick={() => copyText(r.name)} className="text-green-700 hover:underline text-left" title="Copy">
                                                                    {r.name}
                                                                </button>
                                                            </td>

                                                            <td className="px-3 py-3 whitespace-nowrap text-gray-700">
                                                                {r.createdMs ? fmtDateTime(r.createdMs) : (r.createdRaw || 'N/A')}
                                                            </td>

                                                            <td className="px-3 py-3">
                                                                {r.attempts === 0 ? (
                                                                    <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-red-100 text-red-700 font-semibold">
                                                                        <PhoneOff size={12} /> কল হয়নি
                                                                    </span>
                                                                ) : (
                                                                    <div>
                                                                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded font-semibold ${r.answeredCount > 0 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                                                            {r.answeredCount > 0 ? <PhoneIncoming size={12} /> : <PhoneCall size={12} />}
                                                                            {r.attempts} বার try · {r.answeredCount} ধরেছে
                                                                        </span>
                                                                        {last && (
                                                                            <div className="mt-1 text-xs text-gray-500">
                                                                                <span className={`px-1.5 py-0.5 rounded ${STATUS_STYLE[last.status] || 'bg-gray-100 text-gray-600'}`}>{last.status || 'Unknown'}</span>
                                                                                {' '}{fmtDuration(last.duration)}
                                                                                <div>{last.start_time || fmtDateTime(last.ms)}</div>
                                                                                {last.username && <div className="break-all">By: {last.username}</div>}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}
                                                                <button
                                                                    onClick={() => setModal({ type: 'calls', row: r })}
                                                                    className="mt-1.5 inline-flex items-center gap-1 text-xs text-blue-700 border border-blue-300 bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded-lg transition"
                                                                >
                                                                    <History size={12} /> Call Details
                                                                </button>
                                                            </td>

                                                            <td className="px-3 py-3 whitespace-nowrap text-gray-700">{r.serverCallDate}</td>

                                                            <td className="px-3 py-3 whitespace-nowrap">
                                                                <div className="text-xs text-gray-500">Sent: <span className="font-semibold text-gray-800">{r.reminderCount}</span></div>
                                                                <div className="text-xs text-gray-500 mb-1.5">Next: <span className="text-gray-800">{r.nextReminder}</span></div>
                                                                <label className="inline-flex items-center gap-2 cursor-pointer" title="Enable Reminders">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={r.sendReminders}
                                                                        onChange={(e) => toggleReminders(r, e.target.checked)}
                                                                        className="sr-only peer"
                                                                    />
                                                                    <span className="relative w-10 h-5 rounded-full bg-red-200 peer-checked:bg-green-500 transition after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-4 after:h-4 after:rounded-full after:bg-white after:transition peer-checked:after:translate-x-5"></span>
                                                                    <span className="text-xs text-gray-500">{r.sendReminders ? 'On' : 'Off'}</span>
                                                                </label>
                                                            </td>

                                                            <td className="px-3 py-3 whitespace-nowrap text-gray-700">
                                                                {r.lastCommunicateMs ? fmtDateTime(r.lastCommunicateMs) : 'N/A'} ({r.whatsAppCount})
                                                            </td>

                                                            <td className="px-3 py-3">
                                                                <div className="flex flex-wrap gap-0.5 max-w-[190px]">
                                                                    <IconBtn title="WhatsApp মেসেজ পাঠান" onClick={() => sendWhatsApp(r)}><MessageCircle size={18} /></IconBtn>
                                                                    <IconBtn title="View WhatsApp Clicks" onClick={() => setModal({ type: 'clicks', row: r })}><Eye size={18} /></IconBtn>
                                                                    <IconBtn title="Fraud Check" onClick={() => openFraud(r)} tone="red"><ShieldAlert size={18} /></IconBtn>
                                                                    <IconBtn title="Notes" onClick={() => setModal({ type: 'notes', row: r })} tone="orange"><StickyNote size={18} /></IconBtn>
                                                                    <IconBtn title="Call (softphone)" onClick={() => startCall(r)} tone="blue"><PhoneCall size={18} /></IconBtn>
                                                                    <IconBtn title="Order History (Elevate)" onClick={() => setModal({ type: 'orders', row: r })} tone="blue"><ShoppingBag size={18} /></IconBtn>
                                                                </div>
                                                            </td>

                                                            <td className="px-3 py-3">
                                                                {r.cartItems.length === 0 ? 'N/A' : (
                                                                    <div className="max-h-32 overflow-y-auto space-y-1.5 min-w-[220px]">
                                                                        {r.cartItems.map((it, k) => (
                                                                            <div key={k} className="flex items-start gap-2">
                                                                                {it?.image && (
                                                                                    <img
                                                                                        src={it.image} alt=""
                                                                                        className="w-10 h-10 rounded object-cover flex-shrink-0"
                                                                                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                                                                    />
                                                                                )}
                                                                                <span className="text-xs text-gray-700">
                                                                                    {it?.name || 'Unknown Product'} (পরিমাণ: {it?.quantity ?? 0}, মূল্য: {it?.price ?? 0} টাকা)
                                                                                </span>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </td>

                                                            <td className="px-3 py-3 text-center">
                                                                {r.sell ? (
                                                                    <IconBtn title="View Confirmation Info" onClick={() => setModal({ type: 'sell', row: r })}><CheckCircle2 size={22} /></IconBtn>
                                                                ) : (
                                                                    <IconBtn title="নতুন অর্ডার তৈরি করুন" onClick={() => openCreateOrder(r)} tone="blue"><ShoppingCart size={20} /></IconBtn>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {rows.length > visible && (
                                    <div className="p-4 text-center border-t border-gray-100">
                                        <button
                                            onClick={() => setVisible((v) => v + 50)}
                                            className="text-sm font-semibold text-orange-700 bg-orange-100 hover:bg-orange-200 px-5 py-2 rounded-lg transition"
                                        >
                                            আরও দেখান ({rows.length - visible} টি বাকি)
                                        </button>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Modals */}
            {modal?.type === 'calls' && <CallTriesModal row={modal.row} onClose={() => setModal(null)} />}
            {modal?.type === 'notes' && (
                <NotesModal noteDocId={modal.row.clientId} name={modal.row.name} onClose={() => setModal(null)} />
            )}
            {modal?.type === 'clicks' && <WhatsAppClicksModal docId={modal.row.id} onClose={() => setModal(null)} />}
            {modal?.type === 'fraud' && <FraudModal phone={modal.row.phone} onClose={() => setModal(null)} />}
            {modal?.type === 'orders' && <OrdersModal phone={modal.row.phone} onClose={() => setModal(null)} />}
            {modal?.type === 'sell' && (
                <SellInfoModal
                    docId={modal.row.id}
                    row={modal.row}
                    onOpenCalls={(row) => setModal({ type: 'calls', row })}
                    onClose={() => setModal(null)}
                />
            )}

            {/* Toast */}
            {toast && (
                <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] px-4 py-2.5 rounded-lg shadow-lg text-sm text-white ${toast.type === 'err' ? 'bg-red-600' : 'bg-green-600'}`}>
                    {toast.msg}
                </div>
            )}
        </div>
    );
}

export default WithoutOrdersPage;
